/**
 * The tenant alert engine's code about the platform's own scheduler: a keyed
 * cron whose scheduled fire left no run row at all.
 *
 * Fingerprint: PLATFORM scope, entity `vibe_fleet`, entityId `fleet` — one row
 * for the platform, whichever crons it names, because a scheduler that stopped
 * firing is one cause however many crons it silences, and the crons it names
 * ride on the payload.
 */
import type { IssueDefinition } from "../../issue-definition";
import { ISSUE_CATEGORY } from "../../issue-enums";

export const VIBE_ALERT_CRON_ISSUES = {
  /**
   * Paging: a slot with no row is the one cron failure nothing else reports —
   * a FAILED row raises its own issue, a SKIPPED row is the scheduler finding
   * the cron busy, but a fire that never happened writes nothing for anything to
   * read: the scheduler down, the process gone, the lock never taken.
   */
  VIBE_ALERT_CRON_SLOT_MISSED: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "A keyed cron's scheduled fire passed with no execution row at all — not failed, not skipped, never started — and no later run has covered it for longer than the policy allows. A disabled cron never opens this; a run that is late but present clears it before it pages; a long run still holding its lock covers every fire it spans. The payload names each cron, its first missed slot, how many slots it has missed and when its last row started.",
    severity: "high",
    page: true,
    remedy:
      "For each cron on the payload, open the admin Cron Jobs page and its execution history: the last row's time is when the scheduler last reached it. Every cron missing from the same instant is the scheduler or the process that hosts it — check the worker pods are up and their boot log registered the cron. One cron alone is that cron's registration or its schedule. This resolves on its own once every cron's latest due slot has a row."
  }
} as const satisfies Record<string, IssueDefinition>;
